import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BalanceReportFormatFormDto, type BalanceReportFormatFormInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-format/balance-report-format-form.dto';
import type { BalanceReportFormatClassifierFormInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-format/balance-report-format-classifier-form.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { BalanceReportFormatService } from '@zaoblako/web/core/services/finance/balance-report-format.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-balance-report-format-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    ButtonComponent,
    InputTextComponent,
    JoinPipe,
    CardModule,
    FormsModule
  ],
  templateUrl: './balance-report-format-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BalanceReportFormatFormComponent
  extends BaseFormComponent<BalanceReportFormatFormInterface>
  implements OnInit {
  @Input() public readonly index: number;

  public form: BalanceReportFormatFormInterface = {
    name: '',
    incomeClassifiers: [],
    expenseClassifiers: []
  };

  public dto = BalanceReportFormatFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public constructor(
    private readonly balanceReportFormatService: BalanceReportFormatService,
    private readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.loadForm();
    }
  }

  public addIncomeClassifier(): void {
    this.form.incomeClassifiers = [...(this.form.incomeClassifiers || []), { name: '' }];
    this.cdRef.markForCheck();
  }

  public addExpenseClassifier(): void {
    this.form.expenseClassifiers = [...(this.form.expenseClassifiers || []), { name: '' }];
    this.cdRef.markForCheck();
  }

  public removeIncomeClassifier(index: number): void {
    this.form.incomeClassifiers = (this.form.incomeClassifiers || []).filter((_, i) => i !== index);
    this.cdRef.markForCheck();
  }

  public removeExpenseClassifier(index: number): void {
    this.form.expenseClassifiers = (this.form.expenseClassifiers || []).filter((_, i) => i !== index);
    this.cdRef.markForCheck();
  }

  public saveFormat(): void {
    const payload = this.normalizeForm(this.form);
    if (!this.checkValidate(payload).valid) {
      return;
    }

    this.formStoreService.setLoading(true, this.index);
    const entityId = this.formStoreService.getState.forms[this.index]?.entityId;
    const request$ = entityId
      ? this.balanceReportFormatService.updateOne(entityId, payload)
      : this.balanceReportFormatService.createOne(payload);

    request$.subscribe({
      next: (format) => {
        this.formStoreService.setLoading(false, this.index);
        this.errorService.addSuccessMessage(
          entityId ? `Структура "${format.name}" обновлена` : `Структура "${format.name}" создана`
        );
        this.balanceReportFormatService.notifyClassificationTypesChanged();
        this.formStoreService.saveNext(format, this.index);
      },
      error: () => {
        this.formStoreService.setLoading(false, this.index);
      }
    });
  }

  public deleteFormat(): void {
    const entityId = this.formStoreService.getState.forms[this.index]?.entityId;
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить структуру "${this.form.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.balanceReportFormatService.deleteOne(entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Структура "${this.form.name}" удалена`);
            this.balanceReportFormatService.notifyClassificationTypesChanged();
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  private loadForm(): void {
    this.formStoreService.setLoading(true, this.index);
    const entityId = this.formStoreService.getState.forms[this.index]?.entityId;
    this.balanceReportFormatService.findOneForm(entityId).subscribe({
      next: (form) => {
        this.form = {
          ...form,
          incomeClassifiers: form.incomeClassifiers?.length ? [...form.incomeClassifiers] : [],
          expenseClassifiers: form.expenseClassifiers?.length ? [...form.expenseClassifiers] : []
        };
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
      },
      error: () => {
        this.formStoreService.setLoading(false, this.index);
      }
    });
  }

  private normalizeForm(form: BalanceReportFormatFormInterface): BalanceReportFormatFormInterface {
    const normalizeList = (items?: BalanceReportFormatClassifierFormInterface[]) =>
      (items || [])
        .map((item) => ({ name: item?.name?.trim() }))
        .filter((item) => item.name);

    return {
      name: form.name?.trim(),
      incomeClassifiers: normalizeList(form.incomeClassifiers),
      expenseClassifiers: normalizeList(form.expenseClassifiers)
    };
  }
}
